import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Building2, CheckCircle2, Cloud, Image as ImageIcon, KeyRound, QrCode, Save, UploadCloud, WalletCards } from "lucide-react";

const STATIC_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_STATIC_IMAGE_SIZE = 5 * 1024 * 1024;

const EMPTY = {
  provider: "yokke",
  is_production: true,
  has_static_qr_image: false,
  merchant_id: "",
  server_key: "",
  client_key: "",
  yokke_client_key: "",
  yokke_client_secret: "",
  yokke_private_key_pem: "",
  yokke_partner_id: "",
  yokke_merchant_id: "",
  yokke_terminal_id: "",
  yokke_channel_id: "02",
};

export default function QrisSettings() {
  const [outlets, setOutlets] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [settings, setSettings] = useState({ qris_source: "own_dynamic", platform_dynamic: {} });
  const [outletId, setOutletId] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [savingSource, setSavingSource] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [testing, setTesting] = useState(false);
  const [staticFile, setStaticFile] = useState(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState("");
  const [storedPreviewUrl, setStoredPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [o, c, p] = await Promise.all([
        api.get("/outlets"),
        api.get("/qris-config"),
        api.get("/payment-settings"),
      ]);
      setOutlets(o.data || []);
      setConfigs(c.data || []);
      setSettings(p.data || { qris_source: "own_dynamic", platform_dynamic: {} });
      if (!outletId && o.data?.[0]) setOutletId(o.data[0].id);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal memuat pengaturan pembayaran");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!outletId) return;
    const cfg = configs.find((row) => row.outlet_id === outletId);
    if (!cfg) {
      setForm(EMPTY);
      setStaticFile(null);
      return;
    }
    setForm({
      ...EMPTY,
      ...cfg,
      server_key: "",
      yokke_client_secret: "",
      yokke_private_key_pem: "",
      is_production: true,
      has_static_qr_image: Boolean(cfg.has_static_qr_image),
    });
    setStaticFile(null);
  }, [outletId, configs]);

  useEffect(() => {
    if (!staticFile) {
      setLocalPreviewUrl("");
      return undefined;
    }
    const objectUrl = URL.createObjectURL(staticFile);
    setLocalPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [staticFile]);

  useEffect(() => {
    let active = true;
    let objectUrl = "";
    const cfg = configs.find((row) => row.outlet_id === outletId);
    setStoredPreviewUrl("");
    setPreviewLoading(false);
    if (!outletId || !cfg?.has_static_qr_image) return undefined;

    setPreviewLoading(true);
    api.get(`/qris-config/outlet/${outletId}/static-image`, { responseType: "blob" })
      .then((response) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(response.data);
        setStoredPreviewUrl(objectUrl);
      })
      .catch(() => {
        if (active && cfg.provider === "static") toast.error("Preview QRIS statis tidak dapat dimuat");
      })
      .finally(() => { if (active) setPreviewLoading(false); });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [outletId, configs]);

  const currentOutlet = useMemo(() => outlets.find((row) => row.id === outletId), [outlets, outletId]);
  const source = settings.qris_source || "own_dynamic";
  const platform = settings.platform_dynamic || {};
  const previewUrl = localPreviewUrl || storedPreviewUrl;

  const saveSource = async (value) => {
    setSavingSource(true);
    try {
      const response = await api.put("/payment-settings", { qris_source: value });
      setSettings(response.data || settings);
      toast.success(value === "platform_dynamic" ? "QRIS Dinamis KRAYA dipilih" : "QRIS Dinamis milik mitra dipilih");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal menyimpan sumber QRIS");
    } finally {
      setSavingSource(false);
    }
  };

  const selectStaticFile = (event) => {
    const selected = event.target.files?.[0] || null;
    event.target.value = "";
    if (!selected) return;
    if (!STATIC_IMAGE_TYPES.includes(selected.type)) {
      toast.error("Format QRIS harus JPG, PNG, atau WEBP");
      return;
    }
    if (selected.size > MAX_STATIC_IMAGE_SIZE) {
      toast.error("Ukuran gambar QRIS maksimal 5 MB");
      return;
    }
    setStaticFile(selected);
  };

  const saveOwnConfig = async () => {
    if (!outletId) return toast.error("Pilih outlet terlebih dahulu");
    if (form.provider === "static" && !staticFile && !form.has_static_qr_image) {
      return toast.error("Upload gambar QRIS statis terlebih dahulu");
    }

    setSavingConfig(true);
    try {
      if (form.provider === "static" && staticFile) {
        const data = new FormData();
        data.append("file", staticFile);
        await api.post(`/qris-config/outlet/${outletId}/static-image`, data);
      } else {
        await api.put("/qris-config", { level: "outlet", outlet_id: outletId, ...form, is_production: true });
      }
      toast.success(form.provider === "static" ? "QRIS statis outlet berhasil disimpan" : "Konfigurasi QRIS outlet tersimpan");
      setStaticFile(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Konfigurasi QRIS gagal disimpan");
    } finally {
      setSavingConfig(false);
    }
  };

  const testConnection = async () => {
    if (!outletId) return;
    setTesting(true);
    try {
      if (form.provider === "midtrans") {
        await api.post("/qris-config/test-midtrans", { outlet_id: outletId });
        toast.success("Koneksi Midtrans Production valid");
      } else if (form.provider === "yokke") {
        await api.post("/qris-config/test-yokke", { level: "outlet", outlet_id: outletId });
        toast.success("Koneksi Yokke valid");
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Test koneksi gagal");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="tenant-payment-routing-v1">
      <div className="mb-7">
        <div className="overline text-zinc-500">PEMBAYARAN</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Sistem Pembayaran QRIS</h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-zinc-500">
          Pilih apakah transaksi QRIS menggunakan QRIS Dinamis KRAYA/Owner atau QRIS Dinamis milik mitra sendiri.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <button
          type="button"
          disabled={savingSource || !platform.configured}
          onClick={() => saveSource("platform_dynamic")}
          className={`border p-5 text-left transition ${source === "platform_dynamic" ? "border-[#002FA7] bg-blue-50" : "border-border bg-white hover:border-blue-300"} disabled:cursor-not-allowed disabled:opacity-50`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex gap-3"><Cloud className="mt-0.5 h-5 w-5 text-[#002FA7]"/><div>
              <div className="font-bold">QRIS Dinamis KRAYA / Owner</div>
              <div className="mt-1 text-sm text-zinc-500">Dana masuk ke QRIS KRAYA dan dibayarkan ke mitra H+{platform.settlement_days || 1} setelah dipotong MDR.</div>
            </div></div>
            {source === "platform_dynamic" && <CheckCircle2 className="h-5 w-5 text-[#002FA7]"/>}
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
            <div className="bg-white p-3"><div className="text-zinc-500">Provider</div><strong>{platform.provider ? String(platform.provider).toUpperCase() : "Belum siap"}</strong></div>
            <div className="bg-white p-3"><div className="text-zinc-500">MDR</div><strong>{Number(platform.mdr_percent || 0).toLocaleString("id-ID")}%</strong></div>
            <div className="bg-white p-3"><div className="text-zinc-500">Environment</div><strong>PRODUCTION</strong></div>
          </div>
        </button>

        <button
          type="button"
          disabled={savingSource}
          onClick={() => saveSource("own_dynamic")}
          className={`border p-5 text-left transition ${source === "own_dynamic" ? "border-[#002FA7] bg-blue-50" : "border-border bg-white hover:border-blue-300"}`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex gap-3"><WalletCards className="mt-0.5 h-5 w-5 text-[#002FA7]"/><div>
              <div className="font-bold">QRIS Milik Mitra Sendiri</div>
              <div className="mt-1 text-sm text-zinc-500">Dana langsung masuk ke akun merchant milik mitra. Dapat menggunakan QRIS Dinamis atau QRIS Statis hasil upload per outlet.</div>
            </div></div>
            {source === "own_dynamic" && <CheckCircle2 className="h-5 w-5 text-[#002FA7]"/>}
          </div>
        </button>
      </div>

      {source === "platform_dynamic" && (
        <div className="mt-5 border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
          Settlement KRAYA/Owner bersifat H+{platform.settlement_days || 1}. Nominal yang diterima mitra = gross penjualan QRIS - MDR {Number(platform.mdr_percent || 0).toLocaleString("id-ID")}%.
        </div>
      )}

      <section className={`mt-8 border border-border bg-white ${source !== "own_dynamic" ? "opacity-60" : ""}`}>
        <div className="border-b border-border p-5">
          <div className="flex items-center gap-2"><Building2 className="h-5 w-5"/><h2 className="font-display text-2xl font-black tracking-tight">Konfigurasi QRIS Milik Mitra</h2></div>
          <p className="mt-1 text-sm text-zinc-500">Digunakan hanya ketika opsi “QRIS Milik Mitra Sendiri” aktif.</p>
        </div>
        <div className="grid lg:grid-cols-[260px_1fr]">
          <div className="border-r border-border p-5">
            <div className="overline mb-3 text-zinc-500">Outlet</div>
            <div className="space-y-1">
              {outlets.map((outlet) => (
                <button key={outlet.id} onClick={() => setOutletId(outlet.id)} className={`w-full border-l-2 px-3 py-2.5 text-left text-sm ${outletId === outlet.id ? "border-[#002FA7] bg-zinc-50 font-semibold" : "border-transparent hover:bg-zinc-50"}`}>
                  {outlet.name}
                </button>
              ))}
            </div>
          </div>

          <div className="p-5 lg:p-6">
            <div className="mb-4 flex items-center gap-2"><QrCode className="h-5 w-5"/><div className="font-bold">{currentOutlet?.name || "Pilih Outlet"}</div></div>
            <label className="overline">Provider QRIS</label>
            <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className="mt-1 w-full border border-border px-3 py-2.5">
              <option value="yokke">QRIS Dinamis Yokke / Mandiri</option>
              <option value="midtrans">QRIS Dinamis Midtrans - Production Only</option>
              <option value="static">QRIS Statis</option>
            </select>

            {form.provider === "midtrans" && (
              <div className="mt-5 space-y-3 border-l-2 border-violet-600 pl-4">
                <div className="overline text-violet-700">MIDTRANS CORE API - PRODUCTION ONLY</div>
                <div><label className="overline">Server Key</label><input type="password" value={form.server_key || ""} onChange={(e)=>setForm({...form,server_key:e.target.value})} placeholder={form.has_server_key ? "Tersimpan - kosongkan jika tidak diubah" : "Mid-server-..."} className="mt-1 w-full border border-border px-3 py-2 font-mono text-xs"/></div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div><label className="overline">Client Key</label><input value={form.client_key || ""} onChange={(e)=>setForm({...form,client_key:e.target.value})} className="mt-1 w-full border border-border px-3 py-2 font-mono text-xs"/></div>
                  <div><label className="overline">Merchant ID</label><input value={form.merchant_id || ""} onChange={(e)=>setForm({...form,merchant_id:e.target.value})} className="mt-1 w-full border border-border px-3 py-2 font-mono text-xs"/></div>
                </div>
                <div className="text-xs font-semibold text-emerald-700">Environment dikunci ke PRODUCTION.</div>
              </div>
            )}

            {form.provider === "yokke" && (
              <div className="mt-5 space-y-3 border-l-2 border-[#002FA7] pl-4">
                <div className="overline text-[#002FA7]">YOKKE / MANDIRI SNAP</div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div><label className="overline">X-CLIENT-KEY</label><input value={form.yokke_client_key || ""} onChange={(e)=>setForm({...form,yokke_client_key:e.target.value})} className="mt-1 w-full border border-border px-3 py-2 font-mono text-xs"/></div>
                  <div><label className="overline">Client Secret</label><input type="password" value={form.yokke_client_secret || ""} onChange={(e)=>setForm({...form,yokke_client_secret:e.target.value})} placeholder={form.has_yokke_client_secret ? "Tersimpan - kosongkan jika tidak diubah" : ""} className="mt-1 w-full border border-border px-3 py-2 font-mono text-xs"/></div>
                </div>
                <div><label className="overline">Private Key PEM</label><textarea rows={5} value={form.yokke_private_key_pem || ""} onChange={(e)=>setForm({...form,yokke_private_key_pem:e.target.value})} placeholder={form.has_yokke_private_key ? "Tersimpan - kosongkan jika tidak diubah" : "-----BEGIN PRIVATE KEY-----"} className="mt-1 w-full border border-border px-3 py-2 font-mono text-[10px]"/></div>
                <div className="grid md:grid-cols-3 gap-3">
                  <div><label className="overline">X-PARTNER-ID</label><input value={form.yokke_partner_id || ""} onChange={(e)=>setForm({...form,yokke_partner_id:e.target.value})} className="mt-1 w-full border border-border px-3 py-2 font-mono text-xs"/></div>
                  <div><label className="overline">Merchant ID</label><input value={form.yokke_merchant_id || ""} onChange={(e)=>setForm({...form,yokke_merchant_id:e.target.value})} className="mt-1 w-full border border-border px-3 py-2 font-mono text-xs"/></div>
                  <div><label className="overline">Terminal ID</label><input value={form.yokke_terminal_id || ""} onChange={(e)=>setForm({...form,yokke_terminal_id:e.target.value})} className="mt-1 w-full border border-border px-3 py-2 font-mono text-xs"/></div>
                </div>
                <div className="text-xs text-zinc-500">CHANNEL-ID transaksi POS dipaksa <strong>02</strong>.</div>
              </div>
            )}

            {form.provider === "static" && (
              <div className="mt-5 space-y-3 border-l-2 border-emerald-600 pl-4">
                <div className="overline text-emerald-700">QRIS STATIS OUTLET</div>
                <label className="block cursor-pointer border-2 border-dashed border-zinc-300 bg-zinc-50 p-5 text-center hover:border-emerald-600 hover:bg-emerald-50/40">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={selectStaticFile}
                    className="sr-only"
                    data-testid="qris-static-file"
                  />
                  <UploadCloud className="mx-auto h-8 w-8 text-zinc-500"/>
                  <div className="mt-2 text-sm font-semibold">Upload gambar QRIS statis</div>
                  <div className="mt-1 text-xs text-zinc-500">JPG, PNG, atau WEBP · maksimal 5 MB</div>
                  {staticFile && <div className="mt-2 break-all text-xs font-medium text-emerald-700">File dipilih: {staticFile.name}</div>}
                  {!staticFile && form.has_static_qr_image && <div className="mt-2 text-xs font-medium text-emerald-700">Gambar QRIS sudah tersimpan. Pilih file baru untuk mengganti.</div>}
                </label>
                <div className="flex min-h-64 items-center justify-center border border-border bg-white p-3">
                  {previewUrl ? (
                    <img src={previewUrl} alt="Preview QRIS statis" className="h-56 w-56 object-contain"/>
                  ) : previewLoading ? (
                    <div className="text-sm text-zinc-500">Memuat preview QRIS…</div>
                  ) : (
                    <div className="text-center text-zinc-400">
                      <ImageIcon className="mx-auto h-10 w-10"/>
                      <div className="mt-2 text-xs">Belum ada gambar QRIS yang diunggah</div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-amber-700">QRIS statis tidak memiliki inquiry otomatis. Kasir mengonfirmasi pembayaran setelah dana terlihat pada rekening merchant.</p>
              </div>
            )}

            <div className="mt-5 grid gap-2 sm:grid-cols-2">
              <button onClick={saveOwnConfig} disabled={!outletId || savingConfig} className="inline-flex items-center justify-center gap-2 bg-[#002FA7] px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"><Save className="h-4 w-4"/>{savingConfig ? "Menyimpan…" : "Simpan Konfigurasi"}</button>
              {form.provider !== "static" && <button onClick={testConnection} disabled={!outletId || testing} className="inline-flex items-center justify-center gap-2 border border-zinc-900 px-4 py-3 text-sm font-semibold disabled:opacity-50"><KeyRound className="h-4 w-4"/>{testing ? "Mengecek…" : "Test Koneksi"}</button>}
            </div>
          </div>
        </div>
      </section>
      {loading && <div className="mt-4 text-xs text-zinc-400">Memuat konfigurasi…</div>}
    </div>
  );
}
